import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { localized, t } from '@weavedev/lit-i18next';

import { ConfigurationVariantDto2 } from '@/api-sdk';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { ReducedVariants } from './utils';

interface ColorVariants {
  [key: string]: {
    color: string;
    image: ConfigurationVariantDto2['image'];
    sizeVariants: ConfigurationVariantDto2[];
  };
}
@customElement('fg-configuration-quantity-table')
@localized()
export class FgConfigurationQuantityTable extends LitElement {
  @property({ type: Array })
  variants: ConfigurationVariantDto2[] = [];

  @property({ type: Object })
  reducedVariants?: ReducedVariants;

  renderColorNameAndImage({
    color,
    image,
  }: {
    color?: string;
    image?: ConfigurationVariantDto2['image'];
  } = {}) {
    if (!color) {
      return null;
    }

    return html`
      <div class="color-image">
        ${image
          ? html`
              <div class="image">
                <img src=${image} alt=${color} width="64" height="64" />
              </div>
            `
          : nothing}

        <span>${color}</span>
      </div>
    `;
  }

  renderTableHeader({
    reducedVariants,
    highlighted = false,
  }: {
    reducedVariants?: ReducedVariants;
    highlighted?: boolean;
  }) {
    if (!reducedVariants) {
      return null;
    }

    const { colors, sizes, totalQuantity } = reducedVariants;

    return html`
      <tr class=${`head ${highlighted ? 'white' : ''}`}>
        <th class="color">
          ${t('labels.color')}
          <span>(${colors.length})</span>
        </th>
        ${sizes.length
          ? html`
              <th>
                ${t('labels.size')}
                <span>(${sizes.length})</span>
              </th>
            `
          : nothing}
        <th>
          ${t('labels.quantity')}
          <span>(${totalQuantity})</span>
        </th>
        <th>
          <span class="mobile-hidden">${t('labels.pricePerItem')}</span>
          <span class="mobile-show">${t('labels.pricePerItemMb')}</span>
        </th>
        <th class="total">${t('labels.total')}</th>
      </tr>
    `;
  }

  render() {
    const colorVariants = this.variants.reduce(
      (colorVariants: ColorVariants, variant) => {
        const { image, color } = variant;

        if (!color) {
          return colorVariants;
        }

        colorVariants[color.code] = colorVariants[color.code] || {};

        colorVariants[color.code] = {
          color: color.name,
          image,
          sizeVariants: [
            ...(colorVariants[color.code].sizeVariants || []),
            variant,
          ],
        };

        return colorVariants;
      },
      {},
    );

    const { sizes } = this.reducedVariants || {};

    return html`
      <table>
        <thead>
          ${this.renderTableHeader({ reducedVariants: this.reducedVariants })}
        </thead>

        <tbody>
          ${Object.values(colorVariants).map((variant, index) => {
            const { color, image, sizeVariants } = variant;
            const first: boolean = index === 0;
            const highlighted: boolean = index % 2 === 1;

            return html`
              <tr class=${`color ${highlighted ? 'white' : ''}`}>
                <td colspan=${sizes?.length ? '4' : '3'}>
                  ${this.renderColorNameAndImage({ color, image })}
                </td>
              </tr>
              ${this.renderTableHeader({
                highlighted,
                reducedVariants: this.reducedVariants,
              })}

              <tr
                class=${`
                  body
                  ${highlighted ? 'white' : ''}
                  ${first ? 'first' : ''}
                `}
              >
                <td class="color">
                  ${this.renderColorNameAndImage({ color, image })}
                </td>

                <td colspan=${sizes?.length ? '4' : '3'}>
                  <table>
                    ${sizeVariants.map(sizeVariant => {
                      const {
                        size,
                        quantity,
                        allInTotalPrice,
                        allInUnitPrice,
                      } = sizeVariant;

                      return html`
                        <tr>
                          ${size
                            ? html`
                                <td>${size.name}</td>
                              `
                            : nothing}
                          <td>${quantity}</td>
                          <td>${allInUnitPrice.formatted}</td>
                          <td class="total">${allInTotalPrice.formatted}</td>
                        </tr>
                      `;
                    })}
                  </table>
                </td>
              </tr>
            `;
          })}
        </tbody>
      </table>
    `;
  }

  static styles = [
    baseStyles,
    css`
      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: block;
      }
      table {
        width: 100%;
        table-layout: fixed;
        margin-top: 15px;
      }

      thead {
        display: none;
      }

      th,
      tbody td {
        text-align: center;
        padding: 12px;
      }

      th {
        font-size: 0.75rem;
        font-weight: 400;
      }

      th span {
        color: var(--fg-gray--700, var(--fg-fallback-gray--700));
      }

      th.color,
      td.color {
        display: none;
      }

      th.total,
      td.total {
        text-align: right;
        font-weight: 700;
      }

      tbody th,
      tbody td {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
      }

      tbody tr:first-child td:first-child {
        border-top-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      tbody tr:first-child td:last-child {
        border-top-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      tbody tr:last-child td:first-child {
        border-bottom-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      tbody tr:last-child td:last-child {
        border-bottom-left-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
        border-bottom-right-radius: var(
          --fg-borderradius,
          var(--fg-fallback-borderradius)
        );
      }

      tbody tr.head th {
        padding-top: 0;
        padding-bottom: 0;
      }

      tbody tr.body > td:last-child {
        padding-right: 0;
        padding-left: 0;
      }

      tbody tr.white th,
      tbody tr.white td {
        background: #fff;
      }

      tbody table {
        margin: 0;
      }

      tbody table td {
        padding-top: 2px;
        padding-bottom: 2px;
      }

      .color-image {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .color-image .image {
        background: #fff;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(
          --fg-borderradius--sm,
          var(--fg-fallback-borderradius--sm)
        );
        aspect-ratio: 1 / 1;
        position: relative;
        overflow: hidden;
        width: 50px;
      }

      .color-image img {
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: contain;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .mobile-hidden {
          display: block;
        }
        .mobile-show {
          display: none;
        }

        thead {
          display: table-header-group;
        }

        th,
        tbody td {
          padding: 8px;
        }

        th {
          font-size: 0.875rem;
        }

        th span {
          font-size: 0.75rem;
        }

        th.color,
        td.color {
          text-align: left;
          display: table-cell;
          width: 28%;
        }

        th.total,
        td.total {
          padding-right: 20px;
        }

        tbody {
          font-size: 1rem;
        }

        tbody tr.head,
        tbody tr.color {
          display: none;
        }

        tr.body.first td:first-child {
          border-top-left-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
        }

        tr.body.first td:last-child {
          border-top-right-radius: var(
            --fg-borderradius,
            var(--fg-fallback-borderradius)
          );
        }

        .color-image .image {
          width: 64px;
        }

        .mobile-show {
          display: none;
        }
        .mobile-hidden {
          display: block;
        }
      }
    `,
  ];
}
